import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  ExternalLink, 
  Github, 
  Calendar, 
  TrendingUp, 
  Users, 
  Zap,
  BarChart3,
  PieChart,
  Database,
  FileSpreadsheet,
  Eye
} from "lucide-react";

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const projects = [
    {
      id: 1,
      title: "Cyclistic Bike-Share Analysis",
      slug: "cyclistic-bike-share",
      description: "Comprehensive analysis of bike-sharing patterns to understand user behavior and increase annual memberships. Used advanced SQL queries and statistical analysis to identify key insights.",
      longDescription: "This project involved analyzing over 1 million bike trip records to understand the difference between casual riders and annual members. I performed data cleaning, exploratory analysis, and created visualizations to identify patterns in usage by time, location, and user type.",
      category: "case",
      tools: ["Python", "SQL", "Tableau", "Pandas", "Matplotlib"],
      /*duration: "4 weeks",*/
      status: "Completed",
      type: "Case Study",
      highlights: [
        "Analyzed 1M+ bike trip records",
        "Identified 3 key user behavior patterns",
        "Proposed strategies to increase memberships by 25%",
        "Created interactive dashboard for stakeholders"
      ],
      githubUrl: "https://github.com/JatinMukati/Google-Data-Analytics-Capstone-Project",
      liveUrl: "https://public.tableau.com/app/profile/jatin.mukati/viz/GoogleDataAnalyticsCapstoneProjectJM/Dashboard",
      image: "/api/placeholder/400/250"
    },
    {
      id: 2,
      title: "Vendor Performance Analysis",
      slug: "vendor-performance",
      description: "Vendor efficiency and inventory analysis to optimize profitability, reduce risks, and improve purchasing strategies. Built interactive dashboards using SQL, Python, and Power BI.",
      longDescription: "This project focused on analyzing vendor contributions, inventory turnover, and bulk purchasing strategies in the retail domain. I identified $2.71M tied in unsold inventory, highlighted risks from over-reliance on top vendors (65%+ of purchases), and compared profitability between high- and low-performing vendors. Final recommendations included vendor diversification, pricing adjustments, and inventory optimization supported by an interactive Power BI dashboard.",
      category: "business",
      tools: ["SQL", "Python", "Power BI", "Pandas", "Matplotlib"],
      /*duration: "7 weeks",*/
      status: "Completed",
      type: "Business Intelligence",
      highlights: [
        "Identified $2.71M tied in unsold inventory",
        "Top 10 vendors contributed 65%+ of purchases",
        "Bulk purchasing reduced unit costs by 72%",
        "Built interactive Power BI dashboard for executives"
      ],
      githubUrl: "https://github.com/JatinMukati/vendor-performance-analysis",
      liveUrl: "https://app.powerbi.com/view?r=eyJrIjoiODBmMTZkNjgtNDI4MS00M2Y5LWE1OTUtYTViZWUxZGEzY2FjIiwidCI6IjRmMTFlYjZiLWViMmItNDc3MS04M2E5LTE4MTIyOGM1ZDZiMSJ9",
      image: "/api/placeholder/400/250"
    },
    {
      id: 3,
      title: "Global Layoffs Data Analysis",
      slug: "global-layoffs",
      description: "Analysis of global layoff trends across different industries and regions during 2022-2024. Identified patterns and created predictive models for workforce planning.",
      longDescription: "This project examines layoff trends across various industries, company sizes, and geographical regions. I built predictive models to forecast potential future layoffs and created comprehensive reports for HR professionals.",
      category: "analysis",
      tools: ["R", "Power BI", "Excel", "ggplot2", "dplyr"],
      /*duration: "6 weeks",*/
      status: "Completed",
      type: "Data Analysis",
      highlights: [
        "Processed data from 2000+ companies",
        "Built predictive models with 85% accuracy",
        "Created automated reporting system",
        "Presented findings to 100+ HR professionals"
      ],
      /*githubUrl: "https://github.com/jatinmukati/layoffs-analysis",
      liveUrl: "https://app.powerbi.com/layoffs-dashboard",
      image: "/api/placeholder/400/250" */
    },
    {
      id: 4,
      title: "Global Income Inequality Analysis",
      slug: "income-inequality",
      description: "Comprehensive analysis of global income inequality trends using WIID data. Applied advanced cleaning methods and built interactive dashboards with Power BI and Streamlit.",
      longDescription: "As part of the Infosys Springboard Internship 6.0, I worked in a collaborative team project analyzing the World Income Inequality Database (WIID). I individually applied multiple imputation methods (Median, Mode, Interpolation, KNN) to handle 20%+ missing values, detected and resolved outliers, and improved dataset quality. I then designed interactive Power BI dashboards to highlight inequality measures such as Gini, Palma, quintiles, and deciles, and integrated them into a Streamlit app for end-to-end interactivity.",
      category: "analysis",
      tools: ["Python", "Pandas", "Scikit-learn", "Power BI", "Streamlit"],
      duration: "8 weeks",
      status: "In Progress",
      type: "Data Analysis",
      highlights: [
        "Cleaned and transformed WIID dataset (~25,000+ records across 180+ countries)",
        "Applied advanced imputation methods (Median, Mode, Interpolation, KNN)",
        "Designed interactive Power BI dashboards on inequality metrics",
        "Integrated dashboards into Streamlit for stakeholder accessibility"
      ],
      /*githubUrl: "https://github.com/jatinmukati/global-income-inequality",
      liveUrl: "https://inequality-dashboard.streamlit.app",
      image: "/api/placeholder/400/250"*/
    },
    /*{
      id: 4,
      title: "Agriculture Data Analytics Project",
      description: "Crop yield prediction and optimization analysis using machine learning. Helped farmers increase productivity by 15% through data-driven insights.",
      longDescription: "This project focuses on helping farmers optimize crop yields through data analysis. I analyzed weather patterns, soil conditions, and historical yield data to create predictive models and recommendations.",
      category: "prediction",
      tools: ["Python", "Scikit-learn", "Pandas", "NumPy", "Seaborn"],
      duration: "8 weeks",
      status: "In Progress",
      type: "Predictive Analytics",
      highlights: [
        "Analyzed 5 years of agricultural data",
        "Developed ML models for yield prediction",
        "Achieved 92% prediction accuracy",
        "Helped increase farm productivity by 15%"
      ],
      /*githubUrl: "https://github.com/jatinmukati/agriculture-analytics",
      liveUrl: "https://agriculture-dashboard.herokuapp.com",
      image: "/api/placeholder/400/250"
    },
    {
      id: 5,
      title: "E-commerce Sales Dashboard",
      description: "Interactive dashboard for online retail analysis including customer segmentation, product performance, and sales forecasting.",
      longDescription: "Built a comprehensive analytics solution for an e-commerce platform, including customer behavior analysis, product performance tracking, and sales forecasting using historical data.",
      category: "business",
      tools: ["Tableau", "SQL", "Python", "PostgreSQL"],
      duration: "5 weeks",
      status: "In Progress",
      type: "Business Intelligence",
      highlights: [
        "Real-time sales monitoring",
        "Customer segmentation analysis",
        "Product performance tracking",
        "Sales forecasting models"
      ],
      /*githubUrl: "https://github.com/jatinmukati/ecommerce-dashboard",
      liveUrl: "https://public.tableau.com/ecommerce-dashboard",
      image: "/api/placeholder/400/250"
    },
    {
      id: 6,
      title: "Financial Market Analysis",
      description: "Technical analysis of stock market trends and portfolio optimization using quantitative methods and statistical modeling.",
      longDescription: "Comprehensive analysis of financial markets including trend analysis, risk assessment, and portfolio optimization. Used advanced statistical methods to identify investment opportunities.",
      category: "analysis",
      tools: ["Python", "NumPy", "Pandas", "Matplotlib", "Seaborn"],
      duration: "6 weeks",
      status: "In Progress",
      type: "Financial Analysis",
      highlights: [
        "Analyzed 500+ stocks performance",
        "Built risk assessment models",
        "Created portfolio optimization algorithms",
        "Generated 18% higher returns"
      ],
      /*githubUrl: "https://github.com/jatinmukati/financial-analysis",
      liveUrl: "https://financial-dashboard.streamlit.app",
      image: "/api/placeholder/400/250"
    },
    {
      id: 7,
      title: "Healthcare Data Mining",
      description: "Analysis of patient data to identify health trends and improve treatment outcomes using advanced data mining techniques.",
      longDescription: "This project involved analyzing anonymized patient data to identify patterns in health outcomes, treatment effectiveness, and risk factors for various conditions.",
      category: "prediction",
      tools: ["R", "Python", "Tableau", "scikit-learn", "pandas"],
      duration: "10 weeks",
      status: "In Progress",
      type: "Healthcare Analytics",
      highlights: [
        "Analyzed 10,000+ patient records",
        "Identified key health risk factors",
        "Built predictive models for treatment outcomes",
        "Improved patient care efficiency by 20%"
      ],
      /*githubUrl: "https://github.com/jatinmukati/healthcare-analytics",
      liveUrl: "https://healthcare-dashboard.herokuapp.com",
      image: "/api/placeholder/400/250"
    }*/
  ];

  const categories = [
    { value: "all", label: "All Projects", icon: BarChart3 },
    { value: "business", label: "Business Intelligence", icon: TrendingUp },
    { value: "analysis", label: "Data Analysis", icon: PieChart },
    { value: "case", label: "Case Study", icon: Zap },
  ];        /*prediction           Predictive Analytics*/       

  const filteredProjects = selectedCategory === "all" 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "In Progress": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  return (
    <div className="min-h-screen py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12" data-aos="fade-up">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            My Projects
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-400 max-w-2xl lg:max-w-3xl mx-auto px-4 leading-relaxed">
            A collection of data analysis projects showcasing my skills in SQL, Python, 
            Tableau, and machine learning.
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 sm:mb-10 lg:mb-12" data-aos="fade-up" data-aos-delay="100">
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full max-w-2xl sm:max-w-3xl lg:max-w-4xl mx-auto h-auto p-1">
              {categories.map((category) => (
                <TabsTrigger 
                  key={category.value} 
                  value={category.value} 
                  className="flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base"
                >
                  <category.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline truncate">{category.label}</span>
                  <span className="sm:hidden">{category.label.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Projects Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10 lg:mb-12">
          <Card className="glassmorphism" data-aos="fade-up" data-aos-delay="200">
            <CardContent className="p-4 sm:p-6 text-center">
              <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                {projects.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Total Projects
              </div>
            </CardContent>
          </Card>
          <Card className="glassmorphism" data-aos="fade-up" data-aos-delay="300">
            <CardContent className="p-4 sm:p-6 text-center">
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                {projects.filter(p => p.status === "Completed").length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Completed
              </div>
            </CardContent>
          </Card>
          <Card className="glassmorphism" data-aos="fade-up" data-aos-delay="400">
            <CardContent className="p-4 sm:p-6 text-center">
              <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                {projects.filter(p => p.status === "In Progress").length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                In Progress
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {filteredProjects.map((project, index) => (
            <Card key={project.id} className="glassmorphism flex flex-col" data-aos="fade-up" data-aos-delay={index * 100}>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <Badge className={`${getStatusColor(project.status)} w-fit`}>
                    {project.status}
                  </Badge>
                  <Badge className="w-fit">
                    {project.type}
                  </Badge>
                </div>
                <CardTitle className="text-lg sm:text-xl lg:text-2xl leading-tight">{project.title}</CardTitle>
                {project.duration && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                    {project.duration}
                  </div>
                )}
              </CardHeader>
              <CardContent className="flex flex-col flex-grow p-4 sm:p-6">
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                  {project.description}
                </p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm sm:text-base">
                    Key Highlights:
                  </h4>
                  <ul className="space-y-1">
                    {project.highlights.slice(0, 2).map((highlight, index) => (
                      <li key={index} className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2 leading-relaxed">
                        <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full flex-shrink-0 mt-2"></div>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap gap-1 sm:gap-2 mb-4">
                  {project.tools.slice(0, 3).map((tool) => (
                    <Badge key={tool} variant="secondary" className="text-xs sm:text-sm w-fit px-2 py-1">
                      {tool}
                    </Badge>
                  ))}
                  {project.tools.length > 3 && (
                    <Badge variant="secondary" className="text-xs sm:text-sm w-fit px-2 py-1">
                      +{project.tools.length - 3} more
                    </Badge>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                  <Link to={`/projects/${project.slug}`} className="flex-1">
                    <Button size="sm" variant="outline" className="w-full text-xs sm:text-sm py-2">
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                      <span className="hidden sm:inline">View Details</span>
                      <span className="sm:hidden">Details</span>
                    </Button>
                  </Link>
                  {project.githubUrl && (
                    <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button size="sm" variant="outline" className="w-full text-xs sm:text-sm py-2">
                        <Github className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        Code
                      </Button>
                    </a>
                  )}
                  {project.liveUrl && (
                    <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button size="sm" className="w-full text-xs sm:text-sm py-2">
                        <span className="hidden sm:inline">Live Demo</span>
                        <span className="sm:hidden">Demo</span>
                        <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                      </Button>
                    </a>
                  )}
                </div>
             </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 border" data-aos="fade-up">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Interested in Working Together?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                I'm always open to discussing new projects and opportunities in data analysis.
              </p>
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-800">
                <Link to="/contact">
                  Get In Touch
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Projects;